// frontend/src/services/userService.ts
/**
 * User Service
 * Handles all user-related API calls including sync and profile updates.
 *
 * Migrated to use typedApi for type safety.
 */

import { typedApi } from './typedApiClient';
import { getValidAccessToken } from '@/utils/apiClient';
import { API_URL } from '@/utils/constants';
import type { User, UserEnrollment, UserProfile, AuthUser } from '@/types';

/**
 * A FERPA disclosure of the current user's education records (§99.32).
 * Actor identifiers / IP are intentionally not returned by the API.
 */
export interface Disclosure {
  id: number;
  disclosure_type: string; // 'export' | 'admin_view' | 'admin_modify' | 'admin_delete'
  party_type: string;      // 'self' | 'school_official'
  purpose?: string | null;
  records_scope?: string | null;
  created_at: string;
}

// =============================================================================
// TYPES
// =============================================================================

/** Terms of Service / Privacy Policy acceptance (open-access plan §3.3). */
export interface TermsState {
  current_version: string;
  accepted_version: string | null;
  accepted_at: string | null;
  /** True when the user must accept before using the app (TermsGate). */
  required: boolean;
}

export interface SyncUserResponse {
  id: number;
  supabase_user_id: string;
  email: string;
  name?: string;
  major?: string;
  enrollment_start_semester?: string;
  expected_graduation_semester?: string;
  profile_data?: Record<string, unknown>;
  preferences?: Record<string, unknown>;
  role: string;
  terms?: TermsState | null;
}

/**
 * Thrown by syncUser when the backend authenticates the session but returns
 * `action: 'needs_provisioning'` (a valid identity with no account/grant yet).
 * Callers must treat this as "not a user" — NOT fall back to an empty object,
 * which used to be cached as a broken dbUser. See docs/plans/auth-google-sso/.
 */
export class NeedsProvisioningError extends Error {
  readonly email: string;
  /** Why the backend refused an account (open-access plan §3.9): plus_address,
   *  no_grant, not_eligible, unconfirmed, reauth, verification_unavailable or
   *  capacity. Absent from an older backend; treat as no_grant. */
  readonly reason: string;
  /** The email domains whose school currently has open access. */
  readonly eligibleDomains: string[];
  constructor(email: string, reason?: string | null, eligibleDomains?: string[] | null) {
    super('This account has not been provisioned yet.');
    this.name = 'NeedsProvisioningError';
    this.email = email;
    this.reason = reason || 'no_grant';
    this.eligibleDomains = eligibleDomains ?? [];
  }
}

export interface ProfileSettings {
  career_goals?: string[];
  academic_interests?: string[];
  course_load_preference?: 'lighter' | 'normal' | 'heavier';
  additional_context?: string;
}

export interface ResetOptions {
  chat_history?: boolean;
  academic_plans?: boolean;
  programs?: boolean;
}

export interface ResetResponse {
  message: string;
  reset_summary: {
    chat_history_cleared?: boolean;
    plans_cleared?: boolean;
    programs_cleared?: boolean;
  };
}

export interface EmailCheckResponse {
  exists: boolean;
  message?: string;
}

// =============================================================================
// SERVICE CLASS
// =============================================================================

class UserService {
  /**
   * Sync Supabase user with database
   * Creates or updates user in the database and returns full user data
   * Note: This endpoint doesn't require auth (it's called during the auth flow)
   * but we send the token if available for future compatibility.
   * @param supabaseUser - The user object from Supabase auth
   * @returns Response containing user and plans
   */
  async syncUser(supabaseUser: AuthUser): Promise<SyncUserResponse> {
    if (!supabaseUser || !supabaseUser.id || !supabaseUser.email) {
      throw new Error('Invalid Supabase user data');
    }

    try {
      const data = await typedApi.users.sync({
        supabase_user_id: supabaseUser.id,
        email: supabaseUser.email,
        metadata: supabaseUser.user_metadata || {}
      });

      // API returns { action, user: {...}, plans: [...], ... }.
      // SCHEMA GAP (F11): the generated SyncedUser marks every field
      // optional+nullable because the Pydantic response model does, so it
      // cannot satisfy SyncUserResponse (id/supabase_user_id/email/role are
      // required here) without lossy defaulting. Tighten the backend model
      // and this cast goes away — see the S5 report's schema-gap list.
      const response = data as unknown as {
        user: SyncUserResponse | null;
        action: string;
        reason?: string | null;
        eligible_domains?: string[] | null;
        plans: unknown[];
      };

      // Authenticated but not provisioned: the session is valid but there's no
      // account/grant. Signal it explicitly — do NOT flatten a null user into
      // `{}` (that cached a broken, id-less dbUser and let the app proceed).
      if (response.action === 'needs_provisioning' || !response.user?.id) {
        throw new NeedsProvisioningError(
          supabaseUser.email, response.reason, response.eligible_domains);
      }

      // Extract and flatten the user object for the app context
      return {
        ...response.user,
      };
    } catch (error) {
      // needs_provisioning is an expected control-flow signal, not a failure.
      if (error instanceof NeedsProvisioningError) {
        throw error;
      }
      console.error('User sync error:', error);
      throw error;
    }
  }

  /**
   * Update user enrollment dates
   * @param userId - Database user ID (not Supabase UUID)
   * @param startSemester - Enrollment start (e.g., "fall-2024") or null to clear
   * @param expectedGraduation - Expected graduation (e.g., "spring-2028") or null to clear
   * @returns Updated user data
   */
  async updateEnrollment(
    userId: number,
    startSemester: string | null,
    expectedGraduation: string | null
  ): Promise<UserEnrollment> {
    if (!userId) {
      throw new Error('User ID is required');
    }

    // Handle clearing case (both null) - this is allowed
    if (startSemester === null && expectedGraduation === null) {
      // Both are null - proceed with clearing enrollment
    } else {
      // For non-clearing case, both fields are required
      if (!startSemester || !expectedGraduation) {
        throw new Error('Both start semester and expected graduation are required, or both must be null to clear');
      }
    }

    try {
      const data = await typedApi.users.updateEnrollment(userId, {
        start_semester: startSemester,
        expected_graduation: expectedGraduation
      });

      return data as unknown as UserEnrollment;
    } catch (error) {
      console.error('Enrollment update error:', error);
      throw error;
    }
  }

  /**
   * Get user profile
   * @param userId - Database user ID
   * @returns User profile data
   */
  async getProfile(userId: number): Promise<UserProfile> {
    if (!userId) {
      throw new Error('User ID is required');
    }

    try {
      const data = await typedApi.users.getProfile(userId);
      // API returns { profile: {...} } after data unwrap — extract the inner profile
      const response = data as unknown as { profile?: UserProfile };
      return (response.profile || data) as UserProfile;
    } catch (error) {
      console.error('Get profile error:', error);
      throw error;
    }
  }

  /**
   * Update user profile
   * @param userId - Database user ID
   * @param profileData - Profile fields to update
   * @returns Updated user data
   */
  async updateProfile(userId: number, profileData: Partial<UserProfile>): Promise<User> {
    if (!userId) {
      throw new Error('User ID is required');
    }

    try {
      // Use type assertion since local UserProfile may differ from API schema
      const data = await typedApi.users.updateProfile(
        userId,
        profileData as Parameters<typeof typedApi.users.updateProfile>[1]
      );
      // API returns { user: {...} }, extract the user object
      const response = data as unknown as { user: User };
      return response.user;
    } catch (error) {
      console.error('Profile update error:', error);
      throw error;
    }
  }

  /**
   * Check if email exists in the system
   * Note: This endpoint doesn't require auth (it's used during signup)
   * @param email - Email to check
   * @returns Response indicating if email exists
   */
  async checkEmail(email: string): Promise<EmailCheckResponse> {
    if (!email) {
      throw new Error('Email is required');
    }

    try {
      const data = await typedApi.users.checkEmail({ email });
      return data as unknown as EmailCheckResponse;
    } catch (error) {
      console.error('Email check error:', error);
      throw error;
    }
  }

  /**
   * Update profile settings (career goals, academic interests, course load, context)
   * These are stored in the student_profile table for chatbot personalization.
   * @param settings - Profile settings to update
   * @returns Updated settings
   */
  async updateProfileSettings(settings: ProfileSettings): Promise<ProfileSettings> {
    try {
      const data = await typedApi.studentProfile.updateSettings(settings);
      return data as ProfileSettings;
    } catch (error) {
      console.error('Profile settings update error:', error);
      throw error;
    }
  }

  /**
   * Get profile settings (career goals, academic interests, course load, context)
   * @returns Profile settings
   */
  async getProfileSettings(): Promise<ProfileSettings> {
    try {
      const data = await typedApi.studentProfile.getSettings();
      return data as ProfileSettings;
    } catch (error) {
      console.error('Profile settings get error:', error);
      throw error;
    }
  }

  /**
   * Reset user's own data
   * @param supabaseUserId - User's Supabase UUID
   * @param resetOptions - What to reset
   * @returns Reset summary
   */
  async resetOwnData(supabaseUserId: string, resetOptions: ResetOptions): Promise<ResetResponse> {
    if (!supabaseUserId) {
      throw new Error('User ID is required');
    }

    try {
      const data = await typedApi.users.reset({
        supabase_user_id: supabaseUserId,
        reset_options: resetOptions
      });

      return data as unknown as ResetResponse;
    } catch (error) {
      console.error('Self-reset error:', error);
      throw error;
    }
  }

  /**
   * Download a copy of the current user's complete education record (FERPA
   * right to inspect and obtain a copy). Returns the raw file Blob — the export
   * endpoint returns a file attachment, not the standard JSON envelope.
   */
  async fetchDataExport(): Promise<Blob> {
    const token = await getValidAccessToken();
    const response = await fetch(`${API_URL}/api/users/me/export`, {
      method: 'GET',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
      throw new Error(`Data export failed (${response.status})`);
    }
    return response.blob();
  }

  /**
   * Permanently delete the current user's account and all associated data
   * (FERPA/consent workstream). Irreversible; the backend requires the
   * literal confirmation string. Caller signs the user out afterwards.
   */
  async deleteAccount(): Promise<void> {
    const token = await getValidAccessToken();
    const response = await fetch(`${API_URL}/api/users/me/delete-account`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ confirmation: 'DELETE' }),
    });
    if (!response.ok) {
      throw new Error(`Account deletion failed (${response.status})`);
    }
  }

  /**
   * Fetch the current user's disclosure history (FERPA §99.32) — who accessed
   * their education records and why. Self-scoped server-side.
   */
  async fetchDisclosures(): Promise<Disclosure[]> {
    const data = await typedApi.users.getDisclosures();
    return (data?.disclosures ?? []) as Disclosure[];
  }
}

// Export a singleton instance
export const userService = new UserService();

// Also export the class for testing
export default UserService;
